import React, { useState, useEffect, useCallback } from 'react';
import { Scale, Plus } from 'lucide-react';
import litigationEventService from '../../services/litigationEventService';
import LitigationEventForm from './LitigationEventForm';
import LitigationEventTimeline from './LitigationEventTimeline';
import LitigationDeadlines from './LitigationDeadlines';

// Litigation Events section on the case detail page (Session 6B). Composes the entry form,
// the chronological timeline, and the computed-deadline list. All data is tenant-scoped by
// the backend via the auth token.
const CaseLitigationEvents = ({ caseId, caseName }) => {
  const [events, setEvents] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!caseId) return;
    try {
      setLoading(true);
      const [ev, dl] = await Promise.all([
        litigationEventService.listEvents(caseId),
        litigationEventService.listDeadlines(caseId, true), // include resolved (component splits)
      ]);
      setEvents(ev || []);
      setDeadlines(dl || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load litigation events.');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = () => { setShowForm(false); setEditing(null); load(); };
  const handleEdit = (ev) => { setEditing(ev); setShowForm(true); };
  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this litigation event and its computed deadlines?')) return;
    try {
      await litigationEventService.deleteEvent(eventId);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete the event.');
    }
  };
  const handleAcknowledge = async (id) => { await litigationEventService.acknowledgeDeadline(id); load(); };
  const handleResolve = async (id) => { await litigationEventService.resolveDeadline(id); load(); };
  const handleViewEvent = (eventId) => {
    const el = document.getElementById(`litevent-${eventId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-cyan-400');
      setTimeout(() => el.classList.remove('ring-2', 'ring-cyan-400'), 2000);
    }
  };

  return (
    <div className="p-6">
      {(showForm) && (
        <LitigationEventForm
          caseId={caseId}
          event={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onCreated={handleSaved}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Scale className="w-6 h-6 text-cyan-400" />
          <h3 className="text-xl font-semibold text-white">Litigation Events</h3>
          {events.length > 0 && (
            <span className="px-2 py-0.5 bg-cyan-500/20 rounded-full text-xs text-cyan-300 border border-cyan-500/30">
              {events.length} event{events.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg shadow-cyan-500/20">
          <Plus className="w-4 h-4" /> Log Event
        </button>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3 mb-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-8">
          <LitigationDeadlines
            deadlines={deadlines}
            onAcknowledge={handleAcknowledge}
            onResolve={handleResolve}
            onViewEvent={handleViewEvent}
          />
          <div>
            <h4 className="text-lg font-semibold text-white mb-3">Timeline</h4>
            <LitigationEventTimeline events={events} onEdit={handleEdit} onDelete={handleDelete} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseLitigationEvents;
